import { makeStyles } from '@material-ui/styles'
import { useCallback, useState } from 'react'
import PicklistField from 'core/components/validatedForm/DropdownField'
import Info from 'core/components/validatedForm/Info'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import Theme from 'core/themes/model'
import { waitSeconds } from 'openstack/components/infrastructure/blueprint/helpers'
import { getCurrentUser } from 'openstack/helpers'
import { listAllAvailablePorts } from '../networks/actions'
import { detachFixedIpFromVm, refreshVirtualMachine } from './actions'
import FixedIpsPicklist from './FixedIpsPicklist'
import { hasOtherUserVm } from './helpers'

const useStyles = makeStyles<Theme>((theme) => ({
  fields: {
    gap: 16,
    display: 'grid',
  },
}))

export default function RemoveFixedIpDialog({ rows: vms, onClose }) {
  const vm = vms?.[0]
  const classes = useStyles()
  const [submitting, setSubmitting] = useState(false)
  const defaultParams = {
    port: '',
  }
  const { params, getParamsUpdater, setParams } = useParams(defaultParams)
  const { update, error, reset } = useUpdateAction(detachFixedIpFromVm)
  const { update: refreshVm } = useUpdateAction(refreshVirtualMachine)
  const { reload: refreshPorts } = useListAction(listAllAvailablePorts)

  const handleSubmit = useCallback(() => {
    setSubmitting(true)
    const asyncCalls = async () => {
      const { success } = await update({ id: vm.id, portId: params.port })
      await waitSeconds(3) // wait 3 seconds for VM private IP to be updated first
      await refreshVm({ id: vm.id })
      refreshPorts(true, true)
      setSubmitting(false)
      if (success) handleClose(true)
    }
    asyncCalls()
  }, [params, vm])

  const handleClose = (success?: boolean) => {
    setParams(defaultParams)
    reset()
    onClose(success)
  }

  const { role: userRole, userId } = getCurrentUser()

  return (
    <ModalForm
      title="Remove Network Interface"
      onSubmit={handleSubmit}
      onClose={onClose}
      loading={submitting}
      submitting={submitting}
      error={error}
      submitTitle={`Remove Network Interface`}
      panel="dialog"
      open
    >
      <div className={classes.fields}>
        {userRole === 'member' && hasOtherUserVm({ vms, userId }) && (
          <Info error>NOTE: You are performing an operation on another user's VM.</Info>
        )}
        <Text variant="body2">Select a network interface to remove from the selected VM.</Text>
        <PicklistField
          DropdownComponent={FixedIpsPicklist}
          id="port"
          label="Network Interface to Remove"
          tooltip="List of network interfaces attached to the selected VM"
          onChange={getParamsUpdater('port')}
          value={params.port}
          vmId={vm?.id}
          required
        />
      </div>
    </ModalForm>
  )
}
